import { Colors, Lightning, Registry, Router, Storage, VideoPlayer } from "@lightningjs/sdk";
import { Metrics } from "@firebolt-js/sdk";
import { PlayerTop } from "./components/Player/PlayerTop";
import { Loading } from "../../components/feedback/Loading/Loading";
import theme from "../../lib/theme";
import { SettingButton } from "./components/Player/components/SettingButton";
import { PlayerSubtitle } from "./components/Player/components/PlayerSubtitle";
import { PlayerBottom } from "./components/Player/PlayerBottom";
import { SettingsPopup } from "./components/Player/components/SettingsPopup";
import { handleRewind, handleForward, loader, setPlaybackSpeed, unloader, destroyPlayer as destroyBitmovinPlayer } from "../../lib/video-player";
import { getHomePage, getLanguageName, STORAGE_KEYS } from "../../lib/utils";
import { SubtitleTrack } from "bitmovin-player";
import { PlaybackModel } from "../../lib/models";
import { updateProgress, recordVideoWatchtime } from "../../api/mutations";
import configs from "../../lib/configs";
import posthog from "posthog-js";
import { Theme, themeManager } from "../../components/configurations/Theme/ThemeManager";
import { SubtitleTrackInfo, AudioTrackInfo } from "../../lib/video-player/interface";
import { AppEvents } from "../../types/app-events";

interface VideoPlayerEvent {
    event: Event;
    videoElement: HTMLVideoElement;
}

interface PlaybackTemplateSpec extends Lightning.Component.TemplateSpec {
    Overlay: {
        Info: {
            SeriesName: object;
            Name: object;
            Description: object;
        };
    };
    Loading: typeof Loading;
    TopContainer: {
        Top: typeof PlayerTop;
    };
    BottomContainer: {
        Controls: typeof PlayerBottom;
        SettingIcon: typeof SettingButton;
    };
    Subtitles: typeof PlayerSubtitle;
    SettingsPopup: typeof SettingsPopup;
}

interface PlaybackTypeConfig extends Lightning.Component.TypeConfig {
    IsPage: true;
}

const CONTROLS_HIDE_DELAY = 2000;
const LOADING_DELAY = 400;

const MIN_REWIND = 10;
const MAX_REWIND = 300; // 5 min max

export class Playback
    extends Lightning.Component<PlaybackTemplateSpec, PlaybackTypeConfig>
    implements Lightning.Component.ImplementTemplateSpec<PlaybackTemplateSpec>
{
    _duration = 0;
    _currentTime = 0;
    _playing = true;
    _controlsTimeout: undefined | number;
    _loadingTimeout: undefined | number;
    _loadingTimeoutDelay = LOADING_DELAY;

    _loading = true;
    _seeking = false;

    _playTimeout: undefined | number;
    _progressTimeout: undefined | number;

    _movieId: string | number | undefined;
    _shifting = false;
    _shiftTimeout: undefined | number;
    _controlsHideDelay = CONTROLS_HIDE_DELAY;
    _rewindSeconds = MIN_REWIND;

    _boundEventHandler: any;
    _boundPopStateHandler: any;

    static _overlayPadding = 200;

    _startTime: number = 0;
    _movieTitle: string = "";
    _controlsFocusIndex = 0;

    _settingsVisible = false;
    _hasSettings = false;

    _isLive = false;
    private _themeCallback!: (customTheme: Theme | undefined) => void;
    _externalSubtitles: SubtitleTrack[] = [];
    _externalAudio: Array<{ language: string; languageName: string }> = [];

    _movieType = "";
    private _wasPlayingBeforeQuiesce = false;
    private _lastKnownTime = 0;

    private _onPauseQuiesce = () => {
        // remember state
        this._wasPlayingBeforeQuiesce = this._playing;
        this._lastKnownTime = VideoPlayer.currentTime || 0;

        // stop progress timer while background/suspended
        this._cancelLocalProgressTimer();

        // pause via existing path (respects UI etc.)
        if (this._playing) {
            this.$triggerVideoPause();
        }
    };

    private _onResumeOrRestore = () => {
        // try to restore time if drifted and not live
        const now = VideoPlayer.currentTime || 0;
        if (!this._isLive && Math.abs(now - this._lastKnownTime) > 0.3) {
            try { VideoPlayer.seek(this._lastKnownTime); } catch (e) { console.log(e)}
        }

        if (this._wasPlayingBeforeQuiesce && !this._isLive) {
            this.$triggerVideoPlay();
        }
        this._wasPlayingBeforeQuiesce = false;
    };

    private _onStop = () => {
        this._cancelLocalProgressTimer();
        this._destroyPlayer();
    };

    /** NEW: helper to cancel this screen’s progress-save timeout */
    private _cancelLocalProgressTimer() {
        if (this._progressTimeout) {
            Registry.clearTimeout(this._progressTimeout);
            this._progressTimeout = undefined;
        }
    }


    static override _template(): Lightning.Component.Template<PlaybackTemplateSpec> {
        return {
            w: theme.layout.w,
            h: theme.layout.h,

            collision: true,
            Loading: { type: Loading, alpha: 1 },
            Overlay: {
                rect: true,
                w: theme.layout.w,
                h: theme.layout.h,
                color: Colors(theme.colors.bg.playbackOverlay).alpha(0.7).get(),
                alpha: 1,
                Info: {
                    x: 72,
                    w: 1000,
                    h: theme.layout.h - 200,
                    flex: {
                        direction: "column",
                        justifyContent: "flex-end"
                    },
                    SeriesName: {
                        zIndex: 2,
                        text: {
                            fontSize: theme.banner.title.font.size,
                            lineHeight: theme.banner.title.font.size + 4
                        }
                    },
                    Name: {
                        zIndex: 2,
                        text: {
                            fontSize: theme.banner.title.font.size + 4,
                            lineHeight: theme.banner.title.font.size + 8
                        }
                    },
                    Description: {
                        zIndex: 2,
                        w: 1000,
                        text: {
                            fontSize: theme.banner.meta.font.size + 4,
                            lineHeight: theme.banner.meta.font.size + 8,
                            textOverflow: "ellipsis",
                            wordWrap: true,
                            maxLines: 20
                        }
                    }
                }
            },
            TopContainer: {
                collision: true,
                h: this._overlayPadding * 2,
                w: theme.layout.w,
                rect: true,
                colorTop: Colors(theme.colors.bg.playbackOverlay).alpha(0.4).get(),
                colorBottom: Colors(theme.colors.bg.playbackOverlay).alpha(0).get(),
                Top: {
                    type: PlayerTop,
                    x: 72,
                    y: 36,
                    signals: {
                        playerTopHovered: "_handlePlayerTopHovered"
                    }
                }
            },
            Subtitles: {
                type: PlayerSubtitle,
                x: (w: number) => w / 2,
                mountX: 0.5,
                y: theme.layout.h - 140
            },
            BottomContainer: {
                w: theme.layout.w,
                h: this._overlayPadding * 2,
                y: theme.layout.h - this._overlayPadding * 2,
                rect: true,
                colorTop: Colors(theme.colors.bg.playbackOverlay).alpha(0).get(),
                colorBottom: Colors(theme.colors.bg.playbackOverlay).alpha(0.4).get(),
                collision: true,
                Controls: {
                    y: this._overlayPadding * 2 - 82,
                    collision: true,
                    type: PlayerBottom
                    // x: PLAYER_OFFSETS.x,
                    // y: this._overlayPadding
                }
            },
            SettingsPopup: {
                collision: true,
                type: SettingsPopup
            }
        };
    }

    readonly Overlay = this.getByRef("Overlay")!;
    readonly Info = this.Overlay.getByRef("Info")!;
    readonly SeriesName = this.Info.getByRef("SeriesName")!;
    readonly Name = this.Info.getByRef("Name")!;
    readonly Description = this.Info.getByRef("Description")!;

    readonly TopContainer = this.getByRef("TopContainer")!;
    readonly Top = this.TopContainer.getByRef("Top")!;
    // readonly Controls = this.getByRef("Controls")!;
    readonly BottomContainer = this.getByRef("BottomContainer")!;
    readonly Controls = this.BottomContainer.getByRef("Controls")!;
    readonly Loading = this.getByRef("Loading")!;
    readonly SettingsPopup = this.getByRef("SettingsPopup")!;
    readonly Subtitles = this.getByRef("Subtitles")!;

    override _firstActive() {
        window.reloadContinueWatching = true;

        VideoPlayer.consumer(this);
        VideoPlayer.position(0, 0);
        // VideoPlayer.loader(loader);
        // VideoPlayer.unloader(unloader);
    }

    override _init() {
        this._themeCallback = this._onThemeChange.bind(this);
        themeManager.subscribe(this._themeCallback);
    }

    private _onThemeChange(customTheme: Theme | undefined): void {
        this.Overlay.patch({
            Info: {
                SeriesName: {
                    text: {
                        textColor: customTheme?.title ?? Colors(theme.colors.text.primary).get()
                    }
                },
                Name: {
                    text: {
                        textColor: customTheme?.title ?? Colors(theme.colors.text.primary).get()
                    }
                },
                Description: {
                    text: {
                        textColor: customTheme?.paragraph ?? Colors(theme.colors.text.primary).get()
                    }
                }
            }
        });
    }

    override _detach() {
        themeManager.unsubscribe(this._themeCallback);
    }
    override _setup() {
        this._boundEventHandler = this._resumePlaying.bind(this);
        this._boundPopStateHandler = this._handlePopState.bind(this);
    }

    override _active() {
        // this.application.emit("hideBackground");

        this._loadingTimeoutDelay = 0;

        this._setLoadingState();

        this.application.on(AppEvents.PlayerPauseAndQuiesce, this._onPauseQuiesce);
        this.application.on(AppEvents.PlayerMaybeResumeOrRestore, this._onResumeOrRestore);
        this.application.on(AppEvents.PlayerStop, this._onStop);

        Registry.addEventListener(window, "online", this._boundEventHandler);
        Registry.addEventListener(window, "popstate", this._boundPopStateHandler);
    }

    override _inactive() {
        // save in the current state

        this.application.emit("showBackground");

        this.application.off(AppEvents.PlayerPauseAndQuiesce, this._onPauseQuiesce);
        this.application.off(AppEvents.PlayerMaybeResumeOrRestore, this._onResumeOrRestore);
        this.application.off(AppEvents.PlayerStop, this._onStop);

        this._destroyPlayer();

        Registry.removeEventListener(window, "online", this._boundEventHandler);
        Registry.removeEventListener(window, "popstate", this._boundPopStateHandler);

        Metrics.stopContent(this._movieId?.toString());

        this._captureWatchtime();

        // this._saveProgress();
    }

    override _handleBack() {
        // If settings popup is visible, close it first instead of exiting video
        if (this._settingsVisible) {
            this.$hideSettingsPopup();
            return;
        }
        this._exitVideo();
    }

    private _handlePopState() {
        // Handle browser back button - if settings popup is open, close it and prevent navigation
        if (this._settingsVisible) {
            this.$hideSettingsPopup();
            // Don't push a new state here - let $hideSettingsPopup handle the history
            return;
        }
    }

    override _handleEnter() {
        this.Controls.setPlayPauseState();
        this.$triggerVideoPlayPause();
    }

    override _handlePlayPause() {
        this.Controls.setPlayPauseState();

        this.$triggerVideoPlayPause();
    }

    override _handleRight() {
        this.Controls.setForwardState();

        this.$triggerVideoForward();
    }

    override _handleLeft() {
        this.Controls.setRewindState();

        this.$triggerVideoRewind();
    }

    override _handleForward() {
        this.Controls.setForwardState();

        this.$triggerVideoForward();
    }

    override _handleRewind() {
        this.Controls.setRewindState();

        this.$triggerVideoRewind();
    }

    override _handlePlay() {
        this.Controls.setPlayPauseState();

        this.$triggerVideoPlay();
    }

    override _handlePause() {
        this.Controls.setPlayPauseState();

        this.$triggerVideoPause();
    }

    override _handleStop() {
        this.Controls.setPlayPauseState();

        this.$triggerVideoPause();
    }

    override _captureKey() {
        this._setPlaybackStateTimeout();

        return false;
    }

    _exitVideo() {
        this.Loading.patch({
            smooth: {
                alpha: 0
            }
        });

        Registry.clearTimeouts();

        VideoPlayer.pause();
        setPlaybackSpeed(1);

        if (!Router.getHistory().length) {
            // deeplink
            Router.navigate(getHomePage(), false);
        } else {
            const { currentEpisodeIndex, currentSeasonIndex } = this._getCurrentMedia();

            if (currentEpisodeIndex) {
                window.selectedIndex = currentEpisodeIndex;
                window.seasonSelectedIndex = currentSeasonIndex;
            }

            if (window.selectedMovie?.season?.series?.id) {
                Router.navigate(`series/${window.selectedMovie?.season?.series?.id}`, false);
            } else {
                Router.back();
            }
        }
    }

    _handlePlayerTopHovered() {
        this._setState("TopState");
    }

    _setPlaybackStateTimeout() {
        this._clearControlsTimeout();

        this._controlsTimeout = Registry.setTimeout(() => {
            this._controlsTimeout = undefined;

            if (!this._playing || this._loading) return;

            this._clearLoadingTimeout();
            this._setState("PlaybackState");

            this._controlsHideDelay = CONTROLS_HIDE_DELAY;
        }, this._controlsHideDelay);
    }

    _clearControlsTimeout() {
        if (this._controlsTimeout) {
            Registry.clearTimeout(this._controlsTimeout);
        }
    }

    _clearLoadingTimeout() {
        if (this._loadingTimeout) {
            Registry.clearTimeout(this._loadingTimeout);
            this._loadingTimeout = undefined;
        }
    }

    _destroyPlayer() {
        this._duration = 0;

        VideoPlayer.close();
        VideoPlayer.clear();

        // Destroy Bitmovin player instance and close all A/V resources
        // This is required for Comcast certification - A/V resources must be closed when app becomes inactive
        try {
            destroyBitmovinPlayer();
        } catch (error) {
            console.error("Error destroying Bitmovin player:", error);
        }

        // Ensure video element is properly cleaned up
        const videoEl = document.querySelector("video");
        if (videoEl) {
            try {
                videoEl.pause();
                videoEl.src = "";
                videoEl.load();
            } catch (error) {
                console.error("Error cleaning up video element:", error);
            }
        }
    }

    _initPlayer(data: PlaybackModel[]) {
        if (window.selectedMovie) {
            this._movieType = window.selectedMovie.__typename;
            this._isLive = window.selectedMovie.__typename === "LiveStream";

            this._startTime = Date.now();
            this.SeriesName.patch({
                text: window.selectedMovie.series?.title || window.selectedMovie.season?.series?.title || ""
            });

            const { currentEpisode } = this._getCurrentMedia();

            this._movieTitle = currentEpisode?.title || window.selectedMovie.title || "";

            this.Name.patch({
                text: this._movieTitle
            });

            this.Description.patch({
                text: {
                    text: window.selectedMovie.description || ""
                }
            });
        } else {
            this._isLive = false;
        }

        if (this._isLive || !configs.playback.useSettings) {
            this.Controls.hideSettingIcon();
            this._controlsFocusIndex = 1;
            this._hasSettings = false;
            this.Controls.setHasSettings(false);
        } else {
            this._hasSettings = true;
            this.Controls.setHasSettings(true);
            this.Controls.showSettingIcon();
        }

        this.Info.patch({
            h: theme.layout.h - (this._isLive ? 36 : 200)
        });

        this._destroyPlayer();

        const subtitles = data[0]?.subtitles || [];
        let structuredSubtitles: SubtitleTrack[] = [];
        let preferredSubLang: string = "off";

        if (this._hasSettings && subtitles.length > 0) {
            structuredSubtitles = subtitles.map((s) => ({
                lang: s.language,
                url: s.url,
                kind: "subtitle",
                id: `subtitle-${this._movieId}-0`,
                enabled: false,
                label: getLanguageName(s.language) || s.languageName
            }));

            const preferredSubtitle = Storage.get(STORAGE_KEYS.preferredSubtitle) || "";

            if (preferredSubtitle) {
                console.log(`Applying preferred subtitle: ${preferredSubtitle}`);
                this.SettingsPopup?.setSubtitlePreference(preferredSubtitle);
                preferredSubLang = preferredSubtitle;
            }
        }

        // Store initial external tracks
        this._externalSubtitles = structuredSubtitles;

        // Set initial tracks (will be updated when manifest tracks are discovered)
        this._updateAvailableTracks(structuredSubtitles, []);

        VideoPlayer.unloader(unloader);
        VideoPlayer.loader(loader);

        VideoPlayer.open(data[0]!.url, {
            startPosition: data[0]!.content?.progress?.current || 0,
            subtitles: structuredSubtitles,
            preferredSubLang: preferredSubLang,
            drm: data[0]!.drm,
            laUrl: data[0]!.laUrl,
            onCueEnter: (str: string) => {
                this.setSubtitleText(str);
            },
            onTracksDiscovered: (tracks: { subtitles: SubtitleTrackInfo[]; audio: AudioTrackInfo[] }) => {
                console.log("tracks", tracks);
                this._handleTracksDiscovered(tracks);
            }
        });

        this.application.emit("hideBackground");
    }

    _handleCurrentTimeChange(time: number, togglePlay = false, rewind = true) {
        if (rewind) {
            this._shifting = true;
            if (this._shiftTimeout) {
                Registry.clearTimeout(this._shiftTimeout);
                this._shiftTimeout = undefined;
            }
            this._shiftTimeout = Registry.setTimeout(() => {
                this._shiftTimeout = undefined;
                this._shifting = false;
                this._rewindSeconds = MIN_REWIND;
            }, 450);
        }

        // VideoPlayer.pause();
        this._currentTime = Math.min(this._duration, Math.max(0, time));

        this.Controls.patch({
            currentTime: this._currentTime
        });

        if (togglePlay) {
            if (this._playTimeout) clearTimeout(this._playTimeout);
            this._playTimeout = undefined;
            this._playTimeout = Registry.setTimeout(() => {
                this._playTimeout = undefined;

                VideoPlayer.play();
            }, 450);
        }

        if (!this._progressTimeout) {
            this._progressTimeout = Registry.setTimeout(async () => {
                await this._saveProgress();
            }, 5000);
        }
    }

    async _saveProgress() {
        if (this._movieId) {
            try {
                await updateProgress(
                    this._movieId,
                    Math.round(VideoPlayer.currentTime || 0),
                    this._movieType === "TvSeries" ? "Episode" : this._movieType
                );
            } catch (e) {
                console.log(e);
            }
        }

        this._progressTimeout = undefined;
    }

    _setLoadingState() {
        this._clearLoadingTimeout();

        this._loadingTimeout = Registry.setTimeout(() => {
            this._setState("LoadingState");

            this._loadingTimeoutDelay = LOADING_DELAY;
        }, this._loadingTimeoutDelay);
    }

    _setControlsState() {
        this._clearLoadingTimeout();
        if (this._settingsVisible) {
            this._setState("SettingsState");
        } else {
            this._setState("ControlsState");
        }
    }

    _hideControls() {
        if (this._settingsVisible) {
            this._setState("SettingsState");
            return;
        }

        this.Loading.patch({
            smooth: {
                alpha: 0
            }
        });

        this.TopContainer.patch({
            smooth: {
                y: -400
            }
        });

        this.Overlay.patch({
            smooth: {
                alpha: 0
            }
        });

        this.Controls.patch({
            smooth: {
                alpha: 0
            }
        });

        this.BottomContainer.patch({
            smooth: {
                y: 1080
            }
        });
    }

    _resumePlaying() {
        if (!this._playing) return;

        VideoPlayer.play();
    }

    _handleVideoEnd() {
        if (
            window.selectedMovie?.__typename === "TvSeries" ||
            window.selectedMovie?.__typename === "Episode" ||
            window.selectedMovie?.__typename === "Collection"
        ) {
            const { currentEpisodeIndex, currentEpisodeList } = this._getCurrentMedia();

            const nextEp =
                typeof currentEpisodeIndex !== "undefined" ? currentEpisodeList[currentEpisodeIndex + 1] : undefined;

            if (nextEp) {
                Router.navigate(`playback/${nextEp.id}`, false);
            } else {
                this._exitVideo();
            }
        } else {
            this._exitVideo();
        }
    }

    _getCurrentMedia() {
        let currentEpisodeIndex =
            window.selectedMovie?.__typename === "Collection"
                ? window.selectedMovie.items?.findIndex((item) => item.id === this._movieId)
                : undefined;

        const currentSeasonIndex = (window.selectedMovie?.series?.seasons || window.selectedMovie?.seasons)?.findIndex(
            (season) => {
                const index = season.episodes!.findIndex((ep) => ep.id === this._movieId);

                if (index > -1) {
                    currentEpisodeIndex = index;
                    return true;
                } else {
                    return false;
                }
            }
        )!;

        const currentSeason = (window.selectedMovie?.series?.seasons || window.selectedMovie?.seasons || [])[
            currentSeasonIndex
        ];

        const currentEpisodeList =
            window.selectedMovie?.__typename === "Collection" && window.selectedMovie?.items
                ? window.selectedMovie?.items
                : currentSeason?.episodes || [];

        const currentEpisode =
            typeof currentEpisodeIndex === "undefined" ? undefined : currentEpisodeList[currentEpisodeIndex];

        return {
            currentEpisodeIndex,
            currentSeasonIndex,
            currentEpisode,
            currentEpisodeList
        };
    }

    $exitVideo() {
        this._exitVideo();
    }

    $triggerVideoPlayPause() {
        if (this._playing) {
            this.$triggerVideoPause();
        } else {
            this.$triggerVideoPlay();
        }
    }

    $triggerVideoPlay() {
        this._playing = true;

        VideoPlayer.play();

        this.Controls.triggeredPlay();
    }

    $triggerVideoPause() {
        this.Overlay.patch({
            smooth: {
                alpha: 1
            }
        });

        if (this._isLive) return;

        this._playing = false;

        VideoPlayer.pause();

        this.Controls.triggeredPause();
    }

    $triggerVideoRewind() {
        if (this._isLive) return;

        this._seeking = true;
        this._rewindSeconds = Math.min(this._rewindSeconds + 5, MAX_REWIND);

        const currentVideoTime = VideoPlayer.currentTime;
        const rewindTime = Math.max(currentVideoTime - this._rewindSeconds, 0);

        try {
            handleRewind(rewindTime, (isBitmovin) => {
                if (isBitmovin) {
                    this._currentTime = rewindTime;
                    this.Controls.patch({ currentTime: rewindTime });
                    this._handleCurrentTimeChange(rewindTime, this._playing, true);
                } else {
                    VideoPlayer.pause();
                    VideoPlayer.seek(rewindTime);
                    this._handleCurrentTimeChange(this._currentTime - this._rewindSeconds, this._playing, true);
                }
            });
        } catch (error) {
            console.error("Error during VideoPlayer operations:", error);
        }
    }

    $triggerVideoForward() {
        if (this._isLive) return;

        this._seeking = true;
        this._rewindSeconds = Math.min(this._rewindSeconds + 5, MAX_REWIND);

        const currentVideoTime = VideoPlayer.currentTime;
        const forwardTime = Math.min(
            currentVideoTime + this._rewindSeconds,
            this._duration || currentVideoTime + this._rewindSeconds
        );

        try {
            handleForward(forwardTime, (isBitmovin) => {
                if (isBitmovin) {
                    this._currentTime = forwardTime;
                    this.Controls.patch({ currentTime: forwardTime });
                    this._handleCurrentTimeChange(forwardTime, this._playing, true);
                } else {
                    VideoPlayer.pause();
                    VideoPlayer.skip(this._rewindSeconds);
                    this._handleCurrentTimeChange(this._currentTime + this._rewindSeconds, this._playing, true);
                }
            });
        } catch (error) {
            console.error("Error during VideoPlayer operations:", error);
        }
    }

    $videoPlayerTimeUpdate({ videoElement }: VideoPlayerEvent) {
        if (videoElement.duration && !this._duration) {
            this._duration = videoElement.duration;

            this.Controls.patch({
                duration: this._duration
            });
        }

        if (!this._shifting) {
            this._handleCurrentTimeChange(videoElement.currentTime);
        }
    }

    $videoPlayerDurationChange({ videoElement }: VideoPlayerEvent) {
        if (videoElement.duration && !this._duration) {
            this._duration = videoElement.duration;

            this.Controls.patch({
                currentTime: 0,
                duration: this._duration
            });
        }
    }

    $videoPlayerPause() {
        if (this._isLive || this._loading) return;

        this._setControlsState();

        if (this._seeking) return;

        this._playing = false;
        this.Controls.setPlayState(this._playing);

        Metrics.mediaPause(this._movieId!.toString());
    }

    $videoPlayerStop() {
        this._setControlsState();

        this._playing = false;

        this.Controls.setPlayState(this._playing);
    }

    $videoPlayerPlaying() {
        if (this._loading) return;

        this._setPlaybackStateTimeout();

        this._playing = true;

        this.Controls.setPlayState(this._playing);

        this.Overlay.patch({
            smooth: {
                alpha: 0
            }
        });

        Metrics.mediaPlay(this._movieId!.toString());
    }

    $videoPlayerCanPlay() {
        if (this._playing) {
            VideoPlayer.play();
        } else {
            VideoPlayer.pause();
        }

        this._setControlsState();

        this._loading = false;
    }

    $videPlayerLoading() {
        this._setLoadingState();

        // this._playing = false;
    }

    $videoPlayerWaiting() {
        this._loading = true;

        this._setLoadingState();
    }

    $videoPlayerError(errorData?: any) {
        // console.group("🔴 VIDEO PLAYER ERROR DEBUG");
        // console.log("Movie ID:", this._movieId);
        // console.log("Movie Title:", this._movieTitle);
        // console.log("Current Time:", this._currentTime);
        // console.log("Duration:", this._duration);
        // console.log("Is Live:", this._isLive);
        // console.log("Is Playing:", this._playing);
        // console.log("Is Seeking:", this._seeking);
        // console.log("Is Shifting:", this._shifting);
        // console.log("Rewind Seconds:", this._rewindSeconds);
        // console.log("Loading State:", this._loading);
        // console.log("Current State:", this._getState());
        //
        // // VideoPlayer state debugging
        // console.log("VideoPlayer Current Time:", VideoPlayer.currentTime);
        // console.log("VideoPlayer Duration:", VideoPlayer.duration);
        // console.log("VideoPlayer Paused:", VideoPlayer.paused);

        // Error data from the event
        // if (errorData) {
        //     console.log("Error Data:", errorData);
        //     if (errorData.event) {
        //         console.log("Error Event:", errorData.event);
        //         console.log("Error Event Type:", errorData.event.type);
        //         if (errorData.event.target) {
        //             console.log("Error Target:", errorData.event.target);
        //             console.log("Error Target Error:", errorData.event.target.error);
        //         }
        //     }
        //     if (errorData.videoElement) {
        //         console.log("Video Element:", errorData.videoElement);
        //         console.log("Video Element Error:", errorData.videoElement.error);
        //         console.log("Video Element Network State:", errorData.videoElement.networkState);
        //         console.log("Video Element Ready State:", errorData.videoElement.readyState);
        //         console.log("Video Element Current Src:", errorData.videoElement.currentSrc);
        //     }
        // }

        // Network and environment info
        // console.log("Navigator Online:", navigator.onLine);
        // console.log("Connection:", (navigator as any).connection);
        // console.log("User Agent:", navigator.userAgent);
        // console.log("Current URL:", window.location.href);
        // console.log("Timestamp:", new Date().toISOString());
        //
        // // Stack trace for debugging where error originated
        // console.trace("Error stack trace");
        // console.groupEnd();

        // Log to external service if available (posthog)
        try {
            posthog.capture("Video Player Error", {
                movieId: this._movieId,
                movieTitle: this._movieTitle,
                currentTime: this._currentTime,
                duration: this._duration,
                isLive: this._isLive,
                isPlaying: this._playing,
                isSeeking: this._seeking,
                isShifting: this._shifting,
                rewindSeconds: this._rewindSeconds,
                loadingState: this._loading,
                currentState: this._getState(),
                videoPlayerCurrentTime: VideoPlayer.currentTime,
                videoPlayerDuration: VideoPlayer.duration,
                videoPlayerPaused: VideoPlayer.paused,
                navigatorOnline: navigator.onLine,
                userAgent: navigator.userAgent,
                currentUrl: window.location.href,
                errorData: errorData ? JSON.stringify(errorData) : null
            });
        } catch (e) {
            console.warn("Failed to log error to posthog:", e);
        }

        this._loading = true;
        this._setLoadingState();
    }

    $videoPlayerSeeking() {
        this._setLoadingState();
    }

    $videoPlayerSeeked() {
        // Reset seeking flag to allow subsequent seeks
        this._seeking = false;

        if (this._playing) {
            VideoPlayer.play();
        } else {
            console.log("Seeked - Not resuming playback (was paused)");
        }
    }

    $videoPlayerEnded() {
        this.$hideSettingsPopup();
        this._handleVideoEnd();
        this.Overlay.patch({
            smooth: {
                alpha: 1
            }
        });
    }

    _captureWatchtime() {
        const duration = (Date.now() - this._startTime) / 1000; // In seconds
        posthog.capture("Watchtime", {
            duration: duration,
            id: this._movieId,
            title: this._movieTitle
        });

        // Send watchtime to CMS
        const contentId = Number(this._movieId);
        if (contentId) {
            recordVideoWatchtime(contentId, duration);
        }
    }

    static override _states() {
        return [
            class TopState extends this {
                override _getFocused() {
                    return this.Top;
                }

                override _handleDown() {
                    // if (this._hasSettings !== true) {
                    //     this.$setFocusToSettingIcon();
                    // } else {
                    this._setControlsState();
                    // }
                }

                override _handleBack() {
                    if (this._playing) {
                        this._controlsHideDelay = 0;
                        this._setPlaybackStateTimeout();
                    } else {
                        return false;
                    }
                }

                override _handleLeft() {
                    return true;
                }
            },
            class ControlsState extends this {
                override $enter() {
                    this._setPlaybackStateTimeout();
                    this._refocus();
                }

                override _getFocused() {
                    return this.Controls;
                }

                override _handleUp() {
                    this._clearLoadingTimeout();
                    this._setState("TopState");
                }

                override _handleBack() {
                    if (this._playing) {
                        this._controlsHideDelay = 0;
                        this._setPlaybackStateTimeout();
                        return true;
                    } else {
                        return false;
                    }
                }
            },
            // class BottomState extends this {
            //     override _getFocused() {
            //         return this.Controls;
            //     }
            //
            //     override _handleUp() {
            //         this._setControlsState();
            //     }
            // },

            class LoadingState extends this {
                override _getFocused() {
                    if (this._settingsVisible) {
                        return this.SettingsPopup;
                    }

                    this._controlsFocusIndex = 1;
                    return this.Controls;
                }

                override $enter() {
                    if (this.Loading.alpha == 0) {
                        this.Loading.patch({
                            smooth: {
                                alpha: 1
                            }
                        });
                    }
                }

                override $exit() {
                    this._clearLoadingTimeout();

                    this.Loading.patch({
                        smooth: {
                            alpha: 0
                        }
                    });
                    if (this._settingsVisible) {
                        this._setState("SettingsState");
                    }
                }
            },
            class PlaybackState extends this {
                override _captureKey() {
                    // Back button timeout not to trigger controls to exit
                    Registry.setTimeout(() => {
                        this._setControlsState();
                        // this._setState("ControlsState");
                        // this.$setFocusToSettingIcon();
                    }, 10);

                    return false;
                }

                // Allow MediaPlayPause to work in PlaybackState
                override _handlePlayPause() {
                    this.Controls.setPlayPauseState();
                    this.$triggerVideoPlayPause();
                }

                override _handlePlay() {
                    this.Controls.setPlayPauseState();
                    this.$triggerVideoPlay();
                }

                override _handlePause() {
                    this.Controls.setPlayPauseState();
                    this.$triggerVideoPause();
                }

                _handleClick() {
                    this._setControlsState();
                }

                override _getFocused() {
                    return null; //this.Controls;
                }

                override $enter() {
                    this._hideControls();
                }

                override $exit() {
                    this._clearLoadingTimeout();

                    this.TopContainer.patch({
                        smooth: {
                            y: 0
                        }
                    });

                    this.Controls.patch({
                        smooth: {
                            alpha: 1
                        }
                    });

                    this.BottomContainer.patch({
                        smooth: {
                            y: theme.layout.h - 200 * 2
                        }
                    });
                }
            },
            class SettingsState extends this {
                override _getFocused() {
                    return this.SettingsPopup;
                }

                override _handleBack() {
                    this.$hideSettingsPopup();
                }

                // Allow play/pause to work even when settings are open
                override _handlePlayPause() {
                    this.Controls.setPlayPauseState();
                    this.$triggerVideoPlayPause();
                }

                override _handlePlay() {
                    this.Controls.setPlayPauseState();
                    this.$triggerVideoPlay();
                }

                override _handlePause() {
                    this.Controls.setPlayPauseState();
                    this.$triggerVideoPause();
                }

                override _handleRight() {}

                override _handleLeft() {}

                override _handleForward() {}

                override _handleRewind() {}
            }
        ];
    }

    set data(data: PlaybackModel[]) {
        this._initPlayer(data);
    }

    set redirect(redirectToMovie: boolean) {
        if (redirectToMovie) {
            if (Router.getHistory().length > 0) {
                Router.back();
            } else {
                Router.navigate(getHomePage(), false);
            }
        }
    }

    override set params(args: { movieId: string }) {
        this.application.emit("showBackground");

        if (this._movieId) {
            Metrics.stopContent(this._movieId?.toString());

            this._playing = true;
            this.Controls.setPlayState(this._playing);
        }

        Metrics.startContent(args.movieId?.toString());

        this._movieId = args.movieId;

        console.log("args", args);
    }

    $setFocusToSettingIcon() {
        // if (this._getState() !== "LoadingState") {
        // this._setState("ControlsState");
        this._setControlsState();
        this.Controls.$setFocusToSettingIcon();
        this.Controls._refocus();
        // }
    }

    $showSettingsPopup() {
        // if (this._getState() !== "LoadingState") {
        this._setState("SettingsState");
        this._settingsVisible = true;

        // Add a history state when opening settings to enable back button handling
        // Only add if we don't already have a settings state
        if (!window.history.state || !window.history.state.settingsOpen) {
            window.history.pushState({ settingsOpen: true }, "", window.location.href);
        }

        this.SettingsPopup!.open();

        Registry.setTimeout(() => {
            if (this.SettingsPopup) {
                this.SettingsPopup._refocus();
            } else {
                console.warn("SettingsPopup is undefined!");
            }
        }, 10);
        // }
    }

    $hideSettingsPopup() {
        if (this.SettingsPopup) {
            this.SettingsPopup.currentMenu = "main";
            this.SettingsPopup!._index = 0;
            this.SettingsPopup.close();
        }

        // Remove the history state that was added when opening settings
        // Only do this if we're actually closing settings that were opened
        if (this._settingsVisible && window.history.state && window.history.state.settingsOpen) {
            // Use replaceState to clean up the history without adding navigation
            window.history.replaceState(null, "", window.location.href);
        }

        this._settingsVisible = false;
        if (this._getState() !== "LoadingState") {
            this.$setFocusToSettingIcon();
        }
    }

    $setPlaybackSpeed(speed: number) {
        setPlaybackSpeed(speed);
        this.$hideSettingsPopup();
    }

    setSubtitleText(subtitle: string) {
        this.Subtitles?.patch({
            subtitleText: subtitle
        });
    }

    $clearSubtitleText() {
        this.Subtitles?.clearSubtitle();
    }

    _handleTracksDiscovered(tracks: { subtitles: SubtitleTrackInfo[]; audio: AudioTrackInfo[] }) {
        if (!this._hasSettings) return;

        // Convert manifest subtitles to SubtitleTrack format and merge with external
        const manifestSubtitleTracks: SubtitleTrack[] = tracks.subtitles
            .filter((s) => s.lang) // Only include tracks with a language
            .map((s) => ({
                lang: s.lang!,
                url: s.url,
                kind: s.kind || "subtitle",
                id: s.id,
                enabled: s.enabled || false,
                label: s.label || getLanguageName(s.lang!) || s.lang!
            }));

        // Merge external and manifest subtitles, avoiding duplicates by lang
        const allSubtitles = [...this._externalSubtitles];
        manifestSubtitleTracks.forEach((manifestSub) => {
            const existingIndex = allSubtitles.findIndex((ext) => ext.lang === manifestSub.lang);
            if (existingIndex === -1) {
                // Add new manifest subtitle
                allSubtitles.push(manifestSub);
            } else {
                // Prefer external subtitle if exists, but update with manifest info if external lacks URL
                const existing = allSubtitles[existingIndex];
                if (existing && !existing.url && manifestSub.url) {
                    allSubtitles[existingIndex] = {
                        ...existing,
                        url: manifestSub.url,
                        lang: existing.lang || manifestSub.lang,
                        kind: existing.kind || manifestSub.kind,
                        id: existing.id || manifestSub.id,
                        enabled: existing.enabled || manifestSub.enabled || false,
                        label: existing.label || manifestSub.label
                    };
                }
            }
        });

        // Convert manifest audio to expected format and merge with external
        const manifestAudioTracks: Array<{ language: string; languageName: string }> = tracks.audio.map((a) => ({
            language: a.language,
            languageName: a.label || getLanguageName(a.language) || a.language
        }));

        // Merge external and manifest audio, avoiding duplicates by language
        const allAudio = [...this._externalAudio];
        manifestAudioTracks.forEach((manifestAudio) => {
            const existingIndex = allAudio.findIndex((ext) => ext.language === manifestAudio.language);
            if (existingIndex === -1) {
                // Add new manifest audio
                allAudio.push(manifestAudio);
            }
        });

        // Update SettingsPopup with merged tracks
        this._updateAvailableTracks(allSubtitles, allAudio);
    }

    private _updateAvailableTracks(
        subtitles: SubtitleTrack[],
        audio: Array<{ language: string; languageName: string }>
    ) {
        console.log("PB update tracks");
        this.SettingsPopup?.setAvailableSubtitles(subtitles);
        this.SettingsPopup?.setAvailableAudioLanguages(audio);
    }
}
